/* eslint-disable @next/next/no-img-element */
import Link from 'next/link'
import { HiBan } from 'react-icons/hi'
import Button from 'components/Button'
import { ResultTag, ResultTags } from 'components/ResultTag'
import Image from 'components/Image'
import PriceTag from 'components/PriceTag'
import UsdPrice from 'components/UsdPrice'
import getTags from 'helpers/getTags'
import { getAvatarURL } from 'helpers/avatar'
import type { NfdRecord } from 'api/api-client'
import { useRouter } from 'next/router'
import useErrorToast from 'hooks/useErrorToast'
import useTermsOfService from 'hooks/useTermsOfService'

const StateBadge = ({ label, banned }: { label: string; banned?: boolean }) => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium italic bg-gray-100 text-gray-500 whitespace-nowrap">
    {banned && <HiBan className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />}
    {label}
  </span>
)

interface MintResultProps {
  nfd: NfdRecord
  activeWallet: string | undefined
}

export default function MintResult({ nfd, activeWallet = '' }: MintResultProps) {
  const router = useRouter()
  const handleError = useErrorToast()
  const acceptTerms = useTermsOfService()

  const isForSale = nfd.state === 'forSale'
  const isAvailable = nfd.state === 'available'
  const isOwned = nfd.state === 'owned'
  const isReserved = nfd.state === 'reserved'
  const isCurated = nfd.category === 'curated'
  const isOwnedByUser = nfd.owner === activeWallet
  const isReservedForUser = nfd.reservedFor === activeWallet
  const isReservedForSomeoneElse = !!nfd.reservedFor && nfd.reservedFor !== activeWallet
  const walletNotConnected = activeWallet === ''

  const handleClickBuyNow = async (url: string, as?: string) => {
    try {
      await acceptTerms()
      router.push(url, as)
    } catch (e) {
      handleError(e)
    }
  }

  const renderAction = () => {
    // @todo: use <Button /> when <a> is supported
    if (isOwned) {
      if (isOwnedByUser && isForSale) {
        return (
          <Link href="/sell/[name]" as={`/sell/${nfd.name}`} passHref>
            <a className="inline-flex items-center justify-center shadow-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:bg-white disabled:text-gray-700 px-4 py-2 text-base rounded-md">
              <span className="sm:hidden">Manage</span>
              <span className="hidden sm:inline">Manage Sale</span>
            </a>
          </Link>
        )
      }

      return (
        <Link href="/name/[id]" as={`/name/${nfd.name}`} passHref>
          <a className="inline-flex items-center justify-center shadow-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:bg-white disabled:text-gray-700 px-4 py-2 text-base rounded-md">
            <span className="sm:hidden">View</span>
            <span className="hidden sm:inline">View profile</span>
          </a>
        </Link>
      )
    }

    if (isReservedForUser) {
      return (
        <Link href="/name/[id]" as={`/name/${nfd.name}`} passHref>
          <a className="inline-flex items-center justify-center shadow-sm font-medium border border-transparent text-white bg-gradient-to-r from-brand-400 to-brand-600 hover:from-brand-500 hover:to-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:bg-brand-600 px-4 py-2 text-base rounded-md">
            <span className="sm:hidden">Claim</span>
            <span className="hidden sm:inline">Claim now</span>
          </a>
        </Link>
      )
    }

    if (walletNotConnected) {
      return (
        <Button variant="primary" disabled>
          <span className="sm:hidden">Buy</span>
          <span className="hidden sm:inline">Buy Now</span>
        </Button>
      )
    }

    if (isForSale && nfd.sellAmount) {
      return (
        <button
          onClick={() => handleClickBuyNow(`/name/${nfd.name}`)}
          className="inline-flex items-center justify-center shadow-sm font-medium border border-transparent text-white bg-gradient-to-r from-brand-400 to-brand-600 hover:from-brand-500 hover:to-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:bg-brand-600 px-4 py-2 text-base rounded-md"
        >
          <span className="sm:hidden">Buy</span>
          <span className="hidden sm:inline">Buy now</span>
        </button>
      )
    }

    if (isAvailable && nfd.sellAmount && !isCurated) {
      return (
        <button
          onClick={() =>
            handleClickBuyNow(
              `/mint/${nfd.name}?origin=buyNow&price=${nfd.sellAmount}`,
              `/mint/${nfd.name}`
            )
          }
          className="inline-flex items-center justify-center shadow-sm font-medium border border-transparent text-white bg-gradient-to-r from-brand-400 to-brand-600 hover:from-brand-500 hover:to-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:bg-brand-600 px-4 py-2 text-base rounded-md"
        >
          <span className="sm:hidden">Buy</span>
          <span className="hidden sm:inline">Buy now</span>
        </button>
      )
    }

    return <Button disabled>Unavailable</Button>
  }

  const renderBadge = () => {
    if (isOwned) {
      return <StateBadge label="Owned" banned={!isOwnedByUser} />
    }

    if (isReserved) {
      return <StateBadge label="Reserved" banned={isReservedForSomeoneElse} />
    }

    if (isForSale && isReservedForSomeoneElse) {
      return <StateBadge label="Reserved" banned />
    }

    if (isCurated) {
      // catch isAvailable but isCurated here (and don't show price)
      return <StateBadge label="Curated" banned={isAvailable} />
    }

    if ((isAvailable || isForSale) && nfd.sellAmount) {
      return (
        <span className="md:flex md:flex-col md:justify-end">
          <PriceTag price={nfd.sellAmount} className="md:text-base" />
          <UsdPrice price={nfd.sellAmount} className="text-right pr-2.5 mt-1 ml-2 md:ml-0" />
        </span>
      )
    }

    return null
  }

  return (
    <div className="flex items-center px-4 py-4 sm:px-6">
      <div className="min-w-0 flex-1 flex items-center">
        <div className="hidden md:block md:flex-shrink-0">
          <div className="relative h-12 w-12 rounded-full bg-gray-200 overflow-hidden">
            <Image
              className="rounded-full object-cover w-full h-full"
              src={getAvatarURL(nfd)}
              alt=""
              layout="fill"
              objectFit="cover"
              fallbackSrc="/img/nfd-loading-placeholder.jpg"
              loadingSrc="/img/nfd-loading-placeholder.jpg"
            />
          </div>
        </div>
        <div className="min-w-0 flex-1 md:px-4 md:flex md:items-center md:justify-between">
          <p className="md:text-lg font-medium text-gray-900 truncate mr-4">
            <span className="mr-3">{nfd.name}</span>
            <wbr />
            <span className="align-text-bottom">
              {isForSale ? (
                <ResultTag label="For Sale" brand />
              ) : (
                <ResultTags tags={getTags(nfd, { premium: true })} />
              )}
            </span>
          </p>

          <div className="mt-1 md:m-0">{renderBadge()}</div>
        </div>
      </div>

      <div>{renderAction()}</div>
    </div>
  )
}
